import { useCallback, useEffect, useMemo, useState } from "react";
import {
  ShieldCheck, Users, Wallet, Landmark, FileText, Download, Upload, BadgeCheck,
  FileCheck2, CheckCircle2, Clock, AlertTriangle, Lock, ChevronRight, Plus, Trash2,
  Receipt, BarChart3, ListChecks, History, Mail, Calendar, RefreshCw, Bell, X,
} from "lucide-react";
import { useToast } from "../../Context/ToastContext";
import { formatINR } from "../tdsConstants";
import { fetchEmployeeTaxRegister, fetchForm16FinancialYears, downloadForm16, parseTdsApiError } from "../tdsApi";
import {
  QUARTERS, STAGES, currentFinancialYear, currentQuarter,
  computeStageStatuses, buildReturnValidation, challansTotal, duplicateChallanNumbers,
} from "./tdsComplianceModel";
import * as api from "./tdsComplianceApi";
import "../../../styles/tds-compliance.css";

/**
 * Server-backed compliance store. Loads the quarter's lifecycle state from the
 * Phase 2 APIs and exposes the same { state, actions } shape the stage cards
 * already use, so the UI is unchanged while persistence is now real + audited.
 */
function useTdsComplianceData(fy, quarter, toast) {
  const [state, setState] = useState(emptyState());
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloaded, setDownloaded] = useState(false);

  const reload = useCallback(async () => {
    try {
      const [ov, ch, rt, ack, f16, audit] = await Promise.all([
        api.getOverview(fy, quarter),
        api.listChallans(fy, quarter),
        api.getReturn(fy, quarter),
        api.getAck(fy, quarter),
        api.getForm16Status(fy),
        api.listAudit().catch(() => []),
      ]);
      setOverview(ov);
      setState(mapServerState({ ov, ch, rt, ack, f16, audit, downloaded }));
    } catch (e) {
      toast.error(api.tdsErr(e));
    } finally {
      setLoading(false);
    }
  }, [fy, quarter, downloaded, toast]);

  useEffect(() => { setLoading(true); reload(); }, [reload]);

  const run = async (fn, okMsg) => {
    try {
      await fn();
      if (okMsg) toast.success(okMsg);
      await reload();
    } catch (e) {
      toast.error(api.tdsErr(e));
    }
  };

  const actions = {
    markDeposited: (amount, date, note) =>
      run(() => api.markDeposit(fy, quarter, { deposited_amount: Number(amount), deposited_on: date, note }), "Tax deposit recorded."),
    addChallan: (c) =>
      run(() => api.createChallan(fy, quarter, {
        challan_number: c.challanNo, bsr_code: c.bsr, cin: c.cin, challan_date: c.date || null,
        amount: Number(c.amount) || 0, bank: c.bank, remarks: c.remarks, file_url: c.fileName || null,
      }), "Challan added."),
    updateChallan: (id, patch) => {
      const body = {};
      if (patch.status !== undefined) body.status = patch.status;
      if (patch.amount !== undefined) body.amount = Number(patch.amount);
      return run(() => api.updateChallan(id, body));
    },
    deleteChallan: (id) => run(() => api.deleteChallan(id), "Challan removed."),
    generateReturn: () => run(() => api.generateReturn(fy, quarter), "24Q return generated."),
    markDownloaded: () => setDownloaded(true),
    setUploadStatus: (status, note) => run(() => api.setUploadStatus(fy, quarter, { status, note }), "Upload status updated."),
    saveAck: (a) =>
      run(() => api.saveAck(fy, quarter, {
        acknowledgement_number: a.ackNumber, token_number: a.tokenNumber,
        upload_date: a.uploadDate || null, accepted_date: a.acceptedDate || null,
        status: a.status, remarks: a.remarks,
      }), "Acknowledgement saved."),
    setForm16: (patch) => {
      if (patch.emailStatus === "sent") return run(() => api.markForm16Emailed(fy), "Marked as emailed.");
      return run(() => api.recordForm16(fy, { status: "generated", employee_id: patch.employeeId || null }));
    },
  };

  return { state, actions, loading, reload, overview };
}

function emptyState() {
  return {
    deposit: { status: "pending", paidAmount: 0, paidDate: "" },
    challans: [], return: { generated: false, version: 0 },
    upload: { status: "waiting" }, ack: { status: "pending" },
    form16: { generated: false, emailStatus: "not_sent" }, audit: [], download: { downloaded: false },
  };
}

function mapServerState({ ov, ch, rt, ack, f16, audit, downloaded }) {
  const summary = ov?.summary || {};
  const r = rt?.return || null;
  return {
    deposit: {
      status: summary.deposit_status || "pending",
      paidAmount: Number(summary.deposited_amount) || 0,
      paidDate: summary.deposited_on || "",
    },
    challans: (ch?.items || []).map((c) => ({
      id: c.challan_id, challanNo: c.challan_number, bsr: c.bsr_code, cin: c.cin,
      date: c.challan_date, amount: Number(c.amount) || 0, bank: c.bank,
      remarks: c.remarks, fileName: c.file_url, status: c.status,
    })),
    return: r
      ? { generated: (r.current_version || 0) > 0, generatedAt: r.generated_at, generatedBy: r.generated_by, version: r.current_version, validationStatus: r.validation_status }
      : { generated: false, version: 0 },
    upload: { status: summary.upload_status || "waiting" },
    ack: ack
      ? { status: ack.status || "pending", ackNumber: ack.acknowledgement_number, tokenNumber: ack.token_number, uploadDate: ack.upload_date, acceptedDate: ack.accepted_date, remarks: ack.remarks }
      : { status: "pending" },
    form16: { generated: Boolean(f16?.generated), emailStatus: f16?.latest?.email_status || "not_sent" },
    audit: (audit || []).map((a) => ({ ts: a.created_at, actor: a.actor_name, action: a.action, detail: a.entity_id })),
    download: { downloaded: Boolean(downloaded) },
  };
}

const STATUS_META = {
  completed: { label: "Completed", cls: "ok", Icon: CheckCircle2 },
  pending: { label: "Pending", cls: "pending", Icon: Clock },
  error: { label: "Needs attention", cls: "error", Icon: AlertTriangle },
  locked: { label: "Locked", cls: "locked", Icon: Lock },
};
const STAGE_ICONS = {
  payroll: Users, deposit: Wallet, challan: Landmark, generate: FileText,
  download: Download, upload: Upload, ack: BadgeCheck, form16: FileCheck2,
};

function downloadText(name, text, type = "text/plain") {
  const blob = new Blob([text], { type });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}

export default function TdsComplianceCenter() {
  const toast = useToast();
  const [fyList, setFyList] = useState([]);
  const [fy, setFy] = useState(currentFinancialYear());
  const [quarter, setQuarter] = useState(currentQuarter());
  const [tab, setTab] = useState("workflow");
  const [activeStage, setActiveStage] = useState("payroll");

  const [employees, setEmployees] = useState([]);
  const [stats, setStats] = useState({ total: 0 });
  const [loading, setLoading] = useState(true);

  const [notifs, setNotifs] = useState([]);
  const [notifOpen, setNotifOpen] = useState(false);

  const { state, actions, overview, reload } = useTdsComplianceData(fy, quarter, toast);

  // Evaluate + load TDS notifications for the selected FY.
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        await api.evaluateNotifications(fy).catch(() => {});
        const list = await api.listNotifications(false);
        if (alive) setNotifs(list || []);
      } catch { /* non-blocking */ }
    })();
    return () => { alive = false; };
  }, [fy, state]);

  async function dismissNotif(id) {
    try {
      await api.markNotificationRead(id);
      setNotifs((n) => n.filter((x) => x.notification_id !== id));
    } catch { /* ignore */ }
  }
  const unreadCount = notifs.filter((n) => !n.is_read).length;

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const [reg, fys] = await Promise.all([
          fetchEmployeeTaxRegister().catch(() => ({ employees: [], stats: { total: 0 } })),
          fetchForm16FinancialYears().catch(() => []),
        ]);
        if (!alive) return;
        setEmployees(reg.employees || []);
        setStats(reg.stats || { total: 0 });
        const fysArr = Array.isArray(fys) ? fys : fys?.financial_years || [];
        const merged = [...new Set([currentFinancialYear(), ...fysArr])];
        setFyList(merged);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const registerMonthly = useMemo(
    () => employees.reduce((s, e) => s + (Number(e.monthlyTds) || 0), 0),
    [employees]
  );
  // Prefer server overview (ledger-aware) over the local register estimate.
  const payableSource = overview?.source || "estimate";
  const monthlyTdsTotal = overview?.monthly_tds ?? registerMonthly;
  const quarterPayable = overview?.quarter_tds_payable ?? registerMonthly * 3;
  const payrollDone = overview?.payroll_done ?? (stats.total > 0 && registerMonthly > 0);

  const { statuses, challanTotal } = useMemo(
    () => computeStageStatuses(state, { payable: quarterPayable, payrollDone }),
    [state, quarterPayable, payrollDone]
  );

  // compliance score for the selected quarter (completed stages / 8)
  const score = useMemo(() => {
    const done = Object.values(statuses).filter((s) => s === "completed").length;
    return Math.round((done / STAGES.length) * 100);
  }, [statuses]);

  const pendingDeposit = state.deposit?.status === "deposited"
    ? Math.max(0, quarterPayable - (state.deposit.paidAmount || 0))
    : quarterPayable;

  const ctx = {
    fy, quarter, state, actions, toast, employees, monthlyTdsTotal, quarterPayable,
    payrollDone, statuses, challanTotal, stats, reload, payableSource,
  };

  return (
    <div className="tdc">
      {/* HEADER */}
      <header className="tdc-hero">
        <div className="tdc-hero-left">
          <span className="tdc-hero-icon"><ShieldCheck size={22} /></span>
          <div>
            <div className="tdc-eyebrow">Payroll · Statutory Compliance</div>
            <h1 className="tdc-title">TDS Compliance Center</h1>
            <p className="tdc-sub">Track every quarter from payroll to Form 16 — deposit, challans, 24Q, filing and acknowledgement, end to end.</p>
          </div>
        </div>
        <div className="tdc-hero-right">
          <div className="tdc-bell-wrap">
            <button type="button" className="tdc-bell" onClick={() => setNotifOpen((o) => !o)} aria-label="TDS notifications">
              <Bell size={18} />
              {unreadCount > 0 ? <span className="tdc-bell-badge">{unreadCount}</span> : null}
            </button>
            {notifOpen ? (
              <div className="tdc-notif-pop">
                <div className="tdc-notif-head">Notifications {unreadCount > 0 ? `(${unreadCount})` : ""}</div>
                {notifs.length === 0 ? (
                  <div className="tdc-notif-empty">You're all caught up.</div>
                ) : notifs.slice(0, 12).map((n) => (
                  <div key={n.notification_id} className={`tdc-notif tdc-notif-${n.severity}`}>
                    <div className="tdc-notif-body">
                      <div className="tdc-notif-title">{n.title}</div>
                      {n.body ? <div className="tdc-notif-text">{n.body}</div> : null}
                    </div>
                    <button type="button" className="tdc-notif-x" onClick={() => dismissNotif(n.notification_id)} aria-label="Dismiss"><X size={13} /></button>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
          <label className="tdc-select-wrap">
            <span>Financial Year</span>
            <select value={fy} onChange={(e) => setFy(e.target.value)}>
              {fyList.map((y) => <option key={y} value={y}>{`FY ${y}`}</option>)}
            </select>
          </label>
          <label className="tdc-select-wrap">
            <span>Quarter</span>
            <select value={quarter} onChange={(e) => { setQuarter(e.target.value); setActiveStage("payroll"); }}>
              {QUARTERS.map((q) => <option key={q.id} value={q.id}>{`${q.label} · ${q.period}`}</option>)}
            </select>
          </label>
        </div>
      </header>

      {/* KPI ROW */}
      <div className="tdc-kpis">
        <Kpi tone="blue" icon={Users} label="Employees" value={stats.total} sub="in tax register" />
        <Kpi tone="indigo" icon={Wallet} label="Monthly TDS" value={formatINR(monthlyTdsTotal)} sub="current run" />
        <Kpi tone="violet" icon={Receipt} label={`${quarter} TDS payable`} value={formatINR(quarterPayable)} sub={payableSource === "ledger" ? "from payroll ledger" : "estimate (monthly × 3)"} />
        <Kpi tone={pendingDeposit > 0 ? "amber" : "green"} icon={Landmark} label="Pending deposit" value={formatINR(pendingDeposit)} sub={state.deposit?.status === "deposited" ? "deposited" : "not deposited"} />
        <Kpi tone="green" icon={ShieldCheck} label="Compliance score" value={`${score}%`} sub={`${quarter} · FY ${fy}`} />
      </div>

      {/* TABS */}
      <nav className="tdc-tabs">
        {[
          { id: "workflow", label: "Quarter Workflow", Icon: ListChecks },
          { id: "ledger", label: "Employee Tax Ledger", Icon: Users },
          { id: "compliance", label: "Compliance Dashboard", Icon: BarChart3 },
          { id: "reports", label: "Reports", Icon: FileText },
        ].map((t) => (
          <button key={t.id} type="button" className={`tdc-tab ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
            <t.Icon size={15} /> {t.label}
          </button>
        ))}
      </nav>

      {loading ? (
        <div className="tdc-card tdc-loading">Loading compliance data…</div>
      ) : tab === "workflow" ? (
        <div className="tdc-workflow">
          {/* stepper */}
          <aside className="tdc-stepper">
            <div className="tdc-stepper-head">
              <span className="tdc-q-badge">{quarter}</span>
              <div>
                <div className="tdc-stepper-title">{QUARTERS.find((q) => q.id === quarter)?.period}</div>
                <div className="tdc-stepper-sub">Filing due {QUARTERS.find((q) => q.id === quarter)?.dueDay}</div>
              </div>
            </div>
            <ol className="tdc-steps">
              {STAGES.map((st) => {
                const status = statuses[st.key];
                const meta = STATUS_META[status];
                const Icon = STAGE_ICONS[st.key];
                const locked = status === "locked";
                return (
                  <li key={st.key}>
                    <button
                      type="button"
                      className={`tdc-step ${activeStage === st.key ? "active" : ""} st-${meta.cls}`}
                      onClick={() => setActiveStage(st.key)}
                    >
                      <span className="tdc-step-num"><Icon size={15} /></span>
                      <span className="tdc-step-body">
                        <span className="tdc-step-title">{st.n}. {st.title}</span>
                        <span className={`tdc-step-status s-${meta.cls}`}><meta.Icon size={11} /> {meta.label}</span>
                      </span>
                      {!locked ? <ChevronRight size={15} className="tdc-step-arrow" /> : null}
                    </button>
                  </li>
                );
              })}
            </ol>
          </aside>

          {/* active stage card + activity */}
          <div className="tdc-stage-area">
            <StageCard stage={activeStage} ctx={ctx} status={statuses[activeStage]} goTo={setActiveStage} />
            <ActivityFeed audit={state.audit} />
          </div>
        </div>
      ) : tab === "ledger" ? (
        <EmployeeLedger employees={employees} monthsForQuarter={QUARTERS.find((q) => q.id === quarter)?.months || []} />
      ) : tab === "compliance" ? (
        <ComplianceDashboard fy={fy} quarterPayable={quarterPayable} monthlyTdsTotal={monthlyTdsTotal} toast={toast} />
      ) : (
        <ReportsPanel fy={fy} quarter={quarter} toast={toast} />
      )}
    </div>
  );
}

/* ── KPI card ── */
function Kpi({ tone, icon: Icon, label, value, sub }) {
  return (
    <div className={`tdc-kpi tdc-kpi-${tone}`}>
      <div className="tdc-kpi-head"><Icon size={15} /> {label}</div>
      <div className="tdc-kpi-value">{value}</div>
      <div className="tdc-kpi-sub">{sub}</div>
    </div>
  );
}

/* ── Stage card router ── */
function StageCard({ stage, ctx, status, goTo }) {
  if (status === "locked") {
    return (
      <section className="tdc-card tdc-locked-card">
        <Lock size={28} />
        <h3>This step is locked</h3>
        <p>Complete the earlier steps first. Each stage unlocks the next so nothing is filed out of order.</p>
      </section>
    );
  }
  switch (stage) {
    case "payroll": return <PayrollStage ctx={ctx} goTo={goTo} />;
    case "deposit": return <DepositStage ctx={ctx} goTo={goTo} />;
    case "challan": return <ChallanStage ctx={ctx} goTo={goTo} />;
    case "generate": return <GenerateStage ctx={ctx} goTo={goTo} />;
    case "download": return <DownloadStage ctx={ctx} goTo={goTo} />;
    case "upload": return <UploadStage ctx={ctx} goTo={goTo} />;
    case "ack": return <AckStage ctx={ctx} goTo={goTo} />;
    case "form16": return <Form16Stage ctx={ctx} />;
    default: return null;
  }
}

function StageHead({ n, title, desc, status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <div className="tdc-stage-head">
      <div>
        <div className="tdc-stage-step">Step {n} of 8</div>
        <h2 className="tdc-stage-title">{title}</h2>
        <p className="tdc-stage-desc">{desc}</p>
      </div>
      <span className={`tdc-pill s-${meta.cls}`}><meta.Icon size={13} /> {meta.label}</span>
    </div>
  );
}
function Field({ label, children }) {
  return <div className="tdc-field"><span className="tdc-field-label">{label}</span><div className="tdc-field-val">{children}</div></div>;
}

/* ── Stage 1: Payroll ── */
function PayrollStage({ ctx, goTo }) {
  const { stats, monthlyTdsTotal, quarterPayable, payrollDone, payableSource, fy, quarter, toast, reload } = ctx;
  const [syncing, setSyncing] = useState(false);
  const fromLedger = payableSource === "ledger";
  async function sync() {
    try {
      setSyncing(true);
      const res = await api.syncLedger(fy, quarter);
      toast.success(`Synced ${res.rows_written} ledger rows from ${res.periods} payroll period(s).`);
      await reload();
    } catch (e) {
      toast.error(api.tdsErr(e));
    } finally { setSyncing(false); }
  }
  return (
    <section className="tdc-card">
      <StageHead n={1} title="Payroll Completed" status={payrollDone ? "completed" : "pending"}
        desc="Auto-generated from your processed payroll. Sync pulls each month's real TDS into the compliance ledger so the quarter figures are exact." />
      <div className="tdc-grid">
        <Field label="Processed employees">{stats.total}</Field>
        <Field label="Monthly TDS">{formatINR(monthlyTdsTotal)}</Field>
        <Field label={fromLedger ? "Quarter TDS (ledger)" : "Quarter TDS (estimate)"}>{formatINR(quarterPayable)}</Field>
        <Field label="Source">{fromLedger ? <span className="tdc-pill s-ok"><CheckCircle2 size={12} /> Payroll ledger</span> : <span className="tdc-pill s-pending">Estimate ×3</span>}</Field>
        <Field label="Status">{payrollDone ? "Ready" : "No TDS data yet"}</Field>
      </div>
      {fromLedger
        ? <div className="tdc-note" style={{ background: "var(--green-50,#f0fdf4)", borderColor: "var(--green-100,#dcfce7)", color: "var(--green-700,#15803d)" }}><CheckCircle2 size={14} /> Quarter figures are from the real monthly payroll ledger.</div>
        : <div className="tdc-note"><AlertTriangle size={14} /> Showing an estimate (monthly × 3). Click “Sync from payroll” to pull the exact per-month TDS from processed payroll runs.</div>}
      <div className="tdc-stage-foot">
        <button className="tdc-btn tdc-btn-ghost" disabled={syncing} onClick={sync}><RefreshCw size={15} /> {syncing ? "Syncing…" : "Sync from payroll"}</button>
        {payrollDone ? <button className="tdc-btn tdc-btn-primary" onClick={() => goTo("deposit")}>Next: Deposit tax <ChevronRight size={15} /></button> : null}
      </div>
    </section>
  );
}

/* ── Stage 2: Deposit ── */
function DepositStage({ ctx, goTo }) {
  const { state, actions, quarterPayable, quarter } = ctx;
  const deposited = state.deposit?.status === "deposited";
  const [amount, setAmount] = useState(quarterPayable);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  useEffect(() => { setAmount(quarterPayable); }, [quarterPayable]);
  return (
    <section className="tdc-card">
      <StageHead n={2} title="Deposit Tax" status={deposited ? "completed" : "pending"}
        desc="Deposit the deducted tax with the government, then record it here. Deposit is due by the 7th of the following month (quarter filing by the listed date)." />
      <div className="tdc-grid">
        <Field label="Quarter TDS payable">{formatINR(quarterPayable)}</Field>
        <Field label="Filing due date">{QUARTERS.find((q) => q.id === quarter)?.dueDay}</Field>
        <Field label="Deposit status">{deposited ? <span className="tdc-pill s-ok"><CheckCircle2 size={12} /> Deposited</span> : <span className="tdc-pill s-pending"><Clock size={12} /> Pending</span>}</Field>
        <Field label="Paid amount">{deposited ? formatINR(state.deposit.paidAmount) : "—"}</Field>
        <Field label="Pending amount">{deposited ? formatINR(Math.max(0, quarterPayable - state.deposit.paidAmount)) : formatINR(quarterPayable)}</Field>
        <Field label="Paid on">{deposited ? state.deposit.paidDate : "—"}</Field>
      </div>
      {!deposited ? (
        <div className="tdc-inline-form">
          <label>Amount paid <input type="number" value={amount} onChange={(e) => setAmount(Number(e.target.value))} /></label>
          <label>Payment date <input type="date" value={date} onChange={(e) => setDate(e.target.value)} /></label>
          <button className="tdc-btn tdc-btn-primary" onClick={() => { actions.markDeposited(amount, date); ctx.toast.success("Tax deposit recorded."); }}>
            <CheckCircle2 size={15} /> Mark Tax Deposited
          </button>
        </div>
      ) : (
        <div className="tdc-stage-foot">
          <button className="tdc-btn tdc-btn-ghost" onClick={() => actions.resetDeposit()}>Undo</button>
          <button className="tdc-btn tdc-btn-primary" onClick={() => goTo("challan")}>Next: Challan entry <ChevronRight size={15} /></button>
        </div>
      )}
    </section>
  );
}

/* ── Stage 3: Challans ── */
function ChallanStage({ ctx, goTo }) {
  const { state, actions, quarterPayable, toast } = ctx;
  const blank = { bsr: "", challanNo: "", cin: "", date: "", amount: "", bank: "", remarks: "", fileName: "" };
  const [form, setForm] = useState(blank);
  const challans = state.challans || [];
  const total = challansTotal(challans);
  const dupes = duplicateChallanNumbers(challans);
  const canAdd = form.challanNo.trim() && Number(form.amount) > 0;

  function add() {
    if (!canAdd) return;
    actions.addChallan({ ...form, amount: Number(form.amount) });
    setForm(blank);
    toast.success("Challan added.");
  }
  return (
    <section className="tdc-card">
      <StageHead n={3} title="Challan Management" status={challans.length === 0 ? "pending" : dupes.length ? "error" : "completed"}
        desc="Record each challan used to deposit the tax. Multiple challans are supported. Duplicate challan numbers are flagged automatically." />

      <div className="tdc-challan-form">
        <input placeholder="BSR Code" value={form.bsr} onChange={(e) => setForm({ ...form, bsr: e.target.value })} />
        <input placeholder="Challan Number *" value={form.challanNo} onChange={(e) => setForm({ ...form, challanNo: e.target.value })} />
        <input placeholder="CIN" value={form.cin} onChange={(e) => setForm({ ...form, cin: e.target.value })} />
        <input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
        <input type="number" placeholder="Amount *" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
        <input placeholder="Bank" value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} />
        <input placeholder="Remarks" value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} />
        <label className="tdc-file">
          <Upload size={13} /> {form.fileName || "Upload PDF"}
          <input type="file" accept="application/pdf" hidden onChange={(e) => setForm({ ...form, fileName: e.target.files?.[0]?.name || "" })} />
        </label>
        <button className="tdc-btn tdc-btn-primary" disabled={!canAdd} onClick={add}><Plus size={15} /> Add challan</button>
      </div>

      {dupes.length ? <div className="tdc-note err"><AlertTriangle size={14} /> Duplicate challan number(s): {dupes.join(", ")}. Remove duplicates before generating the return.</div> : null}

      <div className="tdc-table-wrap">
        <table className="tdc-table">
          <thead><tr><th>Challan #</th><th>BSR</th><th>CIN</th><th>Date</th><th>Amount</th><th>Bank</th><th>Status</th><th>PDF</th><th></th></tr></thead>
          <tbody>
            {challans.map((c) => (
              <tr key={c.id}>
                <td className="mono">{c.challanNo}</td>
                <td className="mono">{c.bsr || "—"}</td>
                <td className="mono">{c.cin || "—"}</td>
                <td>{c.date || "—"}</td>
                <td>{formatINR(c.amount)}</td>
                <td>{c.bank || "—"}</td>
                <td>
                  <select className="tdc-status-select" value={c.status} onChange={(e) => actions.updateChallan(c.id, { status: e.target.value })}>
                    <option value="pending">Pending</option>
                    <option value="verified">Verified</option>
                    <option value="matched">Matched</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </td>
                <td>{c.fileName ? <span className="tdc-file-chip"><FileText size={12} /> {c.fileName}</span> : "—"}</td>
                <td><button className="tdc-icon-btn danger" onClick={() => actions.deleteChallan(c.id)} title="Delete"><Trash2 size={14} /></button></td>
              </tr>
            ))}
            {challans.length === 0 ? <tr><td colSpan={9} className="tdc-empty">No challans yet. Add the first one above.</td></tr> : null}
          </tbody>
        </table>
      </div>

      <div className="tdc-challan-foot">
        <span>Challan total: <strong>{formatINR(total)}</strong> · Payable: <strong>{formatINR(quarterPayable)}</strong>
          {Math.abs(total - quarterPayable) <= Math.max(1, quarterPayable * 0.01) && total > 0
            ? <span className="tdc-match ok"> ✓ Matched</span>
            : total > 0 ? <span className="tdc-match warn"> ⚠ Mismatch</span> : null}
        </span>
        {challans.length > 0 && dupes.length === 0 ? <button className="tdc-btn tdc-btn-primary" onClick={() => goTo("generate")}>Next: Generate 24Q <ChevronRight size={15} /></button> : null}
      </div>
    </section>
  );
}

/* ── Stage 4: Generate 24Q ── */
function GenerateStage({ ctx, goTo }) {
  const { state, actions, quarterPayable, toast } = ctx;
  const validation = buildReturnValidation(state, { payable: quarterPayable });
  const r = state.return;
  return (
    <section className="tdc-card">
      <StageHead n={4} title="Generate Form 24Q" status={r?.generated ? "completed" : validation.passed ? "pending" : "error"}
        desc="The system gathers TAN, deductor details, employee PAN/salary/TDS, challans and tax regime to assemble the quarterly 24Q return. All checks must pass first." />
      <div className="tdc-validation">
        <div className="tdc-validation-title">Pre-generation validation</div>
        {validation.checks.map((c) => (
          <div key={c.id} className={`tdc-check ${c.ok ? "ok" : "fail"}`}>
            {c.ok ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />}
            <span>{c.label}{c.detail ? <em> — {c.detail}</em> : null}</span>
          </div>
        ))}
      </div>
      {r?.generated ? (
        <div className="tdc-grid" style={{ marginTop: 14 }}>
          <Field label="Generated at">{new Date(r.generatedAt).toLocaleString()}</Field>
          <Field label="Generated by">{r.generatedBy}</Field>
          <Field label="Version">v{r.version}</Field>
          <Field label="Validation">{r.validationStatus}</Field>
        </div>
      ) : null}
      <div className="tdc-note"><AlertTriangle size={14} /> Generation records a validated, versioned 24Q snapshot. Download the deductee dataset on the next step; the signed FVU binary / direct e-filing is the next integration.</div>
      <div className="tdc-stage-foot">
        <button className="tdc-btn tdc-btn-primary" disabled={!validation.passed} onClick={() => { actions.generateReturn("passed"); toast.success("24Q return generated (v" + ((r?.version || 0) + 1) + ")."); }}>
          <FileText size={15} /> {r?.generated ? "Regenerate return" : "Generate return"}
        </button>
        {r?.generated ? <button className="tdc-btn tdc-btn-ghost" onClick={() => goTo("download")}>Next: Download <ChevronRight size={15} /></button> : null}
      </div>
    </section>
  );
}

/* ── Stage 5: Download ── */
function DownloadStage({ ctx, goTo }) {
  const { state, actions, fy, quarter, employees, quarterPayable, toast } = ctx;
  async function dl24q() {
    try {
      await api.downloadReport("24q", fy, quarter);
      actions.markDownloaded("24Q dataset");
      toast.success("24Q dataset downloaded.");
    } catch (e) {
      toast.error(api.tdsErr(e));
    }
  }
  function dlText(kind) {
    const header = `Brixigo — ${kind}\nFY ${fy} · ${quarter}\nGenerated ${new Date().toLocaleString()}\n\n`;
    const body = kind === "Summary"
      ? `Quarter TDS payable: ${formatINR(quarterPayable)}\nChallan total: ${formatINR(challansTotal(state.challans))}\nEmployees: ${employees.length}\n`
      : buildReturnValidation(state, { payable: quarterPayable }).checks.map((c) => `[${c.ok ? "PASS" : "FAIL"}] ${c.label}`).join("\n");
    downloadText(`24Q_${quarter}_${fy}_${kind.replace(/\s+/g, "_")}.txt`, header + body);
    actions.markDownloaded(kind);
  }
  return (
    <section className="tdc-card">
      <StageHead n={5} title="Download Return" status={state.download?.downloaded ? "completed" : "pending"}
        desc="Download the 24Q dataset (deductee + TDS rows) plus supporting reports for your records and for uploading to the portal." />
      <div className="tdc-dl-row">
        <button className="tdc-btn tdc-btn-primary" onClick={dl24q}><Download size={15} /> Download 24Q dataset (CSV)</button>
        <button className="tdc-btn tdc-btn-ghost" onClick={() => dlText("Validation Report")}><Download size={15} /> Validation report</button>
        <button className="tdc-btn tdc-btn-ghost" onClick={() => dlText("Summary")}><Download size={15} /> Summary</button>
      </div>
      <div className="tdc-note"><AlertTriangle size={14} /> The 24Q dataset is the deductee-level data that feeds NSDL’s RPU/FVU utility. The signed binary <code>.fvu</code> file (and direct e-filing) is the next integration step.</div>
      <div className="tdc-stage-foot"><button className="tdc-btn tdc-btn-primary" onClick={() => goTo("upload")}>Next: Upload status <ChevronRight size={15} /></button></div>
    </section>
  );
}

/* ── Stage 6: Upload ── */
function UploadStage({ ctx, goTo }) {
  const { state, actions, fy, quarter, toast, reload } = ctx;
  const [provider, setProvider] = useState(null);
  const [filing, setFiling] = useState(false);
  useEffect(() => { api.getProvider().then(setProvider).catch(() => {}); }, []);
  const opts = [
    { v: "waiting", label: "Waiting for upload" },
    { v: "uploaded", label: "Uploaded" },
    { v: "rejected", label: "Rejected" },
    { v: "accepted", label: "Accepted" },
  ];
  async function fileViaProvider() {
    try {
      setFiling(true);
      const res = await api.fileReturn(fy, quarter);
      toast.success(res?.result?.message || `Routed via '${res.provider}' provider.`);
      await reload();
    } catch (e) {
      toast.error(api.tdsErr(e));
    } finally { setFiling(false); }
  }
  return (
    <section className="tdc-card">
      <StageHead n={6} title="Upload to Income Tax Portal" status={state.upload?.status === "accepted" ? "completed" : state.upload?.status === "rejected" ? "error" : "pending"}
        desc="Upload the generated return to the Income Tax Department portal or your authorised filing provider, then record the outcome here." />
      <div className="tdc-guidance">
        <Upload size={18} />
        <span>
          Active filing provider: <strong>{provider?.provider || "manual"}</strong>
          {provider && !provider.supports_direct_upload
            ? " — direct e-filing is not enabled, so upload the file to the portal yourself and record the status below."
            : " — supports direct upload."}
          {" "}New providers plug into the same adapter with no workflow changes.
        </span>
      </div>
      <div className="tdc-radio-row">
        {opts.map((o) => (
          <button key={o.v} type="button" className={`tdc-radio ${state.upload?.status === o.v ? "active" : ""}`} onClick={() => actions.setUploadStatus(o.v)}>{o.label}</button>
        ))}
        <button type="button" className="tdc-btn tdc-btn-ghost" disabled={filing} onClick={fileViaProvider}>
          <Upload size={14} /> {filing ? "Routing…" : "File via provider"}
        </button>
      </div>
      {state.upload?.status === "accepted" ? <div className="tdc-stage-foot"><button className="tdc-btn tdc-btn-primary" onClick={() => goTo("ack")}>Next: Acknowledgement <ChevronRight size={15} /></button></div> : null}
    </section>
  );
}

/* ── Stage 7: Acknowledgement ── */
function AckStage({ ctx, goTo }) {
  const { state, actions, toast } = ctx;
  const [f, setF] = useState({
    ackNumber: state.ack?.ackNumber || "", tokenNumber: state.ack?.tokenNumber || "",
    uploadDate: state.ack?.uploadDate || "", acceptedDate: state.ack?.acceptedDate || "",
    remarks: state.ack?.remarks || "", status: state.ack?.status || "pending", fileName: state.ack?.fileName || "",
  });
  return (
    <section className="tdc-card">
      <StageHead n={7} title="Acknowledgement" status={state.ack?.status === "filed" ? "completed" : state.ack?.status === "rejected" ? "error" : "pending"}
        desc="Record the filing acknowledgement (token / provisional receipt) returned by the portal. This closes the return for the quarter." />
      <div className="tdc-challan-form ack">
        <input placeholder="Acknowledgement Number" value={f.ackNumber} onChange={(e) => setF({ ...f, ackNumber: e.target.value })} />
        <input placeholder="Token Number" value={f.tokenNumber} onChange={(e) => setF({ ...f, tokenNumber: e.target.value })} />
        <label className="tdc-mini-label">Upload date<input type="date" value={f.uploadDate} onChange={(e) => setF({ ...f, uploadDate: e.target.value })} /></label>
        <label className="tdc-mini-label">Accepted date<input type="date" value={f.acceptedDate} onChange={(e) => setF({ ...f, acceptedDate: e.target.value })} /></label>
        <select value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
          <option value="pending">Pending</option>
          <option value="processing">Under processing</option>
          <option value="filed">Filed</option>
          <option value="rejected">Rejected</option>
        </select>
        <label className="tdc-file"><Upload size={13} /> {f.fileName || "Upload acknowledgement PDF"}<input type="file" accept="application/pdf" hidden onChange={(e) => setF({ ...f, fileName: e.target.files?.[0]?.name || "" })} /></label>
        <input placeholder="Remarks" value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} />
        <button className="tdc-btn tdc-btn-primary" onClick={() => { actions.saveAck(f); toast.success("Acknowledgement saved."); }}><CheckCircle2 size={15} /> Save acknowledgement</button>
      </div>
      {state.ack?.status === "filed" ? <div className="tdc-stage-foot"><button className="tdc-btn tdc-btn-primary" onClick={() => goTo("form16")}>Next: Generate Form 16 <ChevronRight size={15} /></button></div> : null}
    </section>
  );
}

/* ── Stage 8: Form 16 ── */
function Form16Stage({ ctx }) {
  const { state, actions, fy, employees, toast } = ctx;
  const [busy, setBusy] = useState(false);
  const [emp, setEmp] = useState("");
  async function bulk() {
    try {
      setBusy(true);
      await downloadForm16({ financialYear: fy });
      actions.setForm16({ generated: true });
      toast.success("Form 16 ZIP generated.");
    } catch (e) {
      toast.error(await parseTdsApiError(e));
    } finally { setBusy(false); }
  }
  async function single() {
    if (!emp) return;
    try {
      setBusy(true);
      await downloadForm16({ employeeId: emp, financialYear: fy });
      actions.setForm16({ generated: true });
      toast.success("Form 16 generated.");
    } catch (e) {
      toast.error(await parseTdsApiError(e));
    } finally { setBusy(false); }
  }
  return (
    <section className="tdc-card">
      <StageHead n={8} title="Generate Form 16" status={state.form16?.generated ? "completed" : "pending"}
        desc="Generate Form 16 for employees once the year's returns are filed. Individual or bulk; download as ZIP." />
      <div className="tdc-form16-row">
        <div className="tdc-form16-block">
          <div className="tdc-block-title">Individual</div>
          <select value={emp} onChange={(e) => setEmp(e.target.value)}>
            <option value="">Select employee…</option>
            {employees.map((e) => <option key={e.id} value={e.id}>{e.name} ({e.code})</option>)}
          </select>
          <button className="tdc-btn tdc-btn-ghost" disabled={!emp || busy} onClick={single}><FileCheck2 size={15} /> Generate & download</button>
        </div>
        <div className="tdc-form16-block">
          <div className="tdc-block-title">Bulk</div>
          <button className="tdc-btn tdc-btn-primary" disabled={busy} onClick={bulk}><Download size={15} /> {busy ? "Working…" : "Generate all (ZIP)"}</button>
          <button className="tdc-btn tdc-btn-ghost" disabled={!state.form16?.generated} onClick={() => { actions.setForm16({ emailStatus: "sent" }); toast.success("Marked as emailed (delivery wiring is Phase 4)."); }}><Mail size={15} /> Mark emailed</button>
        </div>
      </div>
      <div className="tdc-grid" style={{ marginTop: 14 }}>
        <Field label="Generation status">{state.form16?.generated ? "Generated" : "Not generated"}</Field>
        <Field label="Email status">{state.form16?.emailStatus === "sent" ? "Sent" : "Not sent"}</Field>
        <Field label="Financial year">FY {fy}</Field>
      </div>
      <div className="tdc-note"><AlertTriangle size={14} /> Form 16 PDF/ZIP uses your existing backend generator. Email delivery to employees is wired in Phase 4.</div>
    </section>
  );
}

/* ── Activity feed (audit) ── */
function ActivityFeed({ audit }) {
  return (
    <section className="tdc-card tdc-activity">
      <div className="tdc-activity-head"><History size={16} /> Activity & audit log</div>
      {(!audit || audit.length === 0) ? <p className="tdc-empty">No activity for this quarter yet.</p> : (
        <ul className="tdc-activity-list">
          {audit.map((a, i) => (
            <li key={i}>
              <span className="tdc-act-dot" />
              <div>
                <div className="tdc-act-action">{a.action}{a.detail ? <span className="tdc-act-detail"> · {a.detail}</span> : null}</div>
                <div className="tdc-act-meta">{a.actor} · {new Date(a.ts).toLocaleString()}</div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/* ── Employee Tax Ledger ── */
function EmployeeLedger({ employees, monthsForQuarter }) {
  const [q, setQ] = useState("");
  const rows = employees.filter((e) => `${e.name} ${e.code}`.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <section className="tdc-card">
      <div className="tdc-stage-head">
        <div><h2 className="tdc-stage-title">Employee Tax Ledger</h2><p className="tdc-stage-desc">Per-employee TDS across the year. Monthly figures populate from the Phase 2 ledger.</p></div>
        <div className="tdc-search"><input placeholder="Search employee…" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      </div>
      <div className="tdc-table-wrap">
        <table className="tdc-table">
          <thead><tr><th>Employee</th><th>Regime</th><th>Monthly salary</th><th>Monthly TDS</th><th>Quarter TDS</th><th>Annual TDS</th><th>Form 16</th><th>{monthsForQuarter.join(" · ")}</th></tr></thead>
          <tbody>
            {rows.map((e) => (
              <tr key={e.id}>
                <td><div className="tdc-emp"><span className="tdc-emp-name">{e.name}</span><span className="tdc-emp-code">{e.code}</span></div></td>
                <td><span className={`tdc-regime ${e.regime}`}>{e.regime === "old" ? "Old" : "New"}</span></td>
                <td>{formatINR(Math.round((e.grossIncome || 0) / 12))}</td>
                <td>{formatINR(e.monthlyTds)}</td>
                <td>{formatINR((e.monthlyTds || 0) * 3)}</td>
                <td>{formatINR(e.annualTax)}</td>
                <td><span className="tdc-pill s-pending">Pending</span></td>
                <td className="tdc-timeline">{monthsForQuarter.map((m) => <span key={m} className="tdc-tl-cell" title={`${m}: ${formatINR(e.monthlyTds)}`}>{m[0]}</span>)}</td>
              </tr>
            ))}
            {rows.length === 0 ? <tr><td colSpan={8} className="tdc-empty">No employees found.</td></tr> : null}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* ── Compliance dashboard (charts) — live from the Phase 2 APIs ── */
function ComplianceDashboard({ fy, quarterPayable, toast }) {
  const [data, setData] = useState([]);
  const [trend, setTrend] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const [results, tr] = await Promise.all([
          Promise.all(QUARTERS.map(async (qq) => {
            const [ov, ack] = await Promise.all([
              api.getOverview(fy, qq.id).catch(() => ({})),
              api.getAck(fy, qq.id).catch(() => null),
            ]);
            const summary = ov?.summary || {};
            return {
              q: qq.id,
              payable: Number(ov?.quarter_tds_payable) || 0,
              filed: ack?.status === "filed",
              pendingDeposit: summary.deposit_status !== "deposited",
            };
          })),
          api.getTrend(fy).catch(() => []),
        ]);
        if (alive) { setData(results); setTrend(tr || []); }
      } catch (e) {
        toast.error(api.tdsErr(e));
      }
    })();
    return () => { alive = false; };
  }, [fy, toast]);
  const filed = data.filter((d) => d.filed).length;
  const maxTrend = Math.max(1, ...trend.map((t) => Number(t.tds) || 0));
  const hasTrend = trend.some((t) => Number(t.tds) > 0);
  const maxPayable = Math.max(1, ...data.map((d) => d.payable));
  return (
    <div className="tdc-charts">
      <section className="tdc-card">
        <h3 className="tdc-chart-title"><BarChart3 size={16} /> Quarter payable & filing</h3>
        <div className="tdc-bars">
          {data.map((d) => (
            <div key={d.q} className="tdc-bar-col">
              <div className="tdc-bar-track"><div className={`tdc-bar-fill${d.filed ? "" : " alt"}`} style={{ height: `${(d.payable / maxPayable) * 100}%` }} /></div>
              <div className="tdc-bar-label">{d.q}{d.filed ? " ✓" : ""}</div>
              <div className="tdc-bar-val">{formatINR(d.payable)}</div>
            </div>
          ))}
        </div>
      </section>
      <section className="tdc-card">
        <h3 className="tdc-chart-title"><Calendar size={16} /> Monthly TDS trend{hasTrend ? "" : " (no ledger yet)"}</h3>
        <div className="tdc-bars wide">
          {(trend.length ? trend : ["Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec","Jan","Feb","Mar"].map((m) => ({ month: m, tds: 0 }))).map((t) => (
            <div key={t.month} className="tdc-bar-col" title={`${t.month}: ${formatINR(t.tds)}`}>
              <div className="tdc-bar-track"><div className="tdc-bar-fill alt" style={{ height: `${Number(t.tds) > 0 ? Math.max(6, (Number(t.tds) / maxTrend) * 100) : 4}%` }} /></div>
              <div className="tdc-bar-label sm">{t.month[0]}</div>
            </div>
          ))}
        </div>
        <p className="tdc-chart-note">{hasTrend ? "Real month-wise TDS from the payroll ledger." : "Run “Sync from payroll” on the Payroll step to populate the monthly ledger."}</p>
      </section>
      <section className="tdc-card tdc-mini-stats">
        <h3 className="tdc-chart-title"><ShieldCheck size={16} /> Filing status</h3>
        <div className="tdc-ms-row"><span>Filed returns</span><strong className="ok">{filed}</strong></div>
        <div className="tdc-ms-row"><span>Pending returns</span><strong className="warn">{4 - filed}</strong></div>
        <div className="tdc-ms-row"><span>Outstanding deposits</span><strong className="warn">{data.filter((d) => d.pendingDeposit).length}</strong></div>
        <div className="tdc-ms-row"><span>Quarter payable</span><strong>{formatINR(quarterPayable)}</strong></div>
      </section>
    </div>
  );
}

/* ── Reports — server-generated CSV via the Phase 2 report service ── */
function ReportsPanel({ fy, quarter, toast }) {
  const dl = async (kind, label) => {
    try {
      await api.downloadReport(kind, fy, quarter);
      toast.success(`${label} exported.`);
    } catch (e) {
      toast.error(api.tdsErr(e));
    }
  };
  const reports = [
    { name: "Form 24Q Dataset", desc: "Deductee-level data (feeds NSDL RPU/FVU)", ready: true, fn: () => dl("24q", "24Q dataset") },
    { name: "Employee TDS Register", desc: "Per-employee TDS for the year", ready: true, fn: () => dl("employee-register", "Employee TDS register") },
    { name: "Challan Register", desc: "All challans for this quarter", ready: true, fn: () => dl("challan-register", "Challan register") },
    { name: "Audit Report", desc: "Full action history (who/when)", ready: true, fn: () => dl("audit", "Audit report") },
    { name: "Monthly TDS Register", desc: "Month-wise deductions from the ledger", ready: true, fn: () => dl("monthly-register", "Monthly TDS register") },
    { name: "Quarterly Summary", desc: "Quarter totals & filing status", ready: true, fn: () => dl("quarterly-summary", "Quarterly summary") },
    { name: "Filing Status Report", desc: "Return + acknowledgement status", ready: true, fn: () => dl("filing-status", "Filing status report") },
    { name: "Form 16 Report", desc: "Form 16 generation status", ready: true, fn: () => dl("form16-report", "Form 16 report") },
  ];
  return (
    <section className="tdc-card">
      <div className="tdc-stage-head"><div><h2 className="tdc-stage-title">Reports</h2><p className="tdc-stage-desc">Server-generated CSV exports for the full compliance lifecycle. Excel/PDF formatting can layer on top later.</p></div></div>
      <div className="tdc-reports-grid">
        {reports.map((r) => (
          <div key={r.name} className={`tdc-report ${r.ready ? "" : "soon"}`}>
            <div className="tdc-report-icon"><FileText size={18} /></div>
            <div className="tdc-report-body"><div className="tdc-report-name">{r.name}</div><div className="tdc-report-desc">{r.desc}</div></div>
            {r.ready ? <button className="tdc-btn tdc-btn-ghost tdc-btn-sm" onClick={r.fn}><Download size={14} /> CSV</button> : <span className="tdc-soon-badge">Phase 2</span>}
          </div>
        ))}
      </div>
    </section>
  );
}
